import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { HelmetHead } from '~/components/common';
import { exitGame } from '~/api';
import missionGameKey from '~/features/missionGame/missionGameKey';
import useModal from '~/hooks/useModal';
import { GameDetailData, ModalParams } from '~/types';
import { PAGE_ROUTE } from '~/constants';
import { breakpoint } from '~/styles/themes';
import { useUserStore } from '~/store';
import useMissionGameStore from '~/store/useMissionGameStore';
import useToast from '~/store/useToast';

export default function GameMainPage({
  gameDetails,
}: {
  gameDetails: GameDetailData;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const email = useUserStore((state) => state.email);

  const setAnswerContext = useMissionGameStore(
    (state) => state.setAnswerContext,
  );
  const { showToast } = useToast();
  const { openModal } = useModal();

  const modalData: ModalParams = {
    title: 'Want to Exit Game?',
    content: 'Click to Exit Game.',
    click: () => handleExitGame(),
    buttonName: 'Exit',
  };

  const handleStartGame = () => {
    navigate(`/mission/${gameDetails.game_id}`, {
      state: {
        isMissionCompleted: false,
      },
    });
  };

  const handleExitGame = async () => {
    const request = {
      email,
      game_id: gameDetails.game_id,
    };
    const { status } = await exitGame(request);
    if (status === 200) {
      // Game EXIT => INITIALIZE GAME DATA (Mission Answer Info, ChatRoom Info)
      setAnswerContext([]);
      queryClient.invalidateQueries({
        queryKey: missionGameKey.getGameChatroomList(request),
      });

      navigate(`${PAGE_ROUTE.MISSION_GAME}/detail/${gameDetails.game_id}`);
      return;
    }
    showToast('error', 'something went wrong');
  };

  return (
    <>
      <HelmetHead title='Mission Game' />
      <Container>
        <ImageBackground
          src={gameDetails?.thumbnail_file_path}
          alt='game background'
        />
        <BlurContainer>
          <BackgroundContainer />
        </BlurContainer>

        <GameIntroContainer>
          <Title>{gameDetails.title}</Title>
          <Intro>
            {gameDetails.introduce}
            <br />
            <br />
            {gameDetails.mission_info}
          </Intro>
          <ButtonContainer>
            <Button text='START' type='light' handleClick={handleStartGame} />
            <Button
              text='EXIT'
              type='dark'
              handleClick={() => openModal(modalData)}
            />
          </ButtonContainer>
        </GameIntroContainer>
      </Container>
    </>
  );
}
export const Button = ({
  type,
  text,
  handleClick,
  width = null,
}: {
  type: 'light' | 'dark';
  text: string;
  handleClick: () => void;
  width?: number | null;
}) => {
  return (
    <StyledButton type={type} onClick={handleClick} width={width}>
      {text}
    </StyledButton>
  );
};

const BlurContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  flex-direction: column;
  justify-content: end;
  width: 100%;
  height: 60%;
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  -webkit-mask: linear-gradient(180deg, transparent, black 35%);
  gap: 10px;
`;
const ImageBackground = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  padding-bottom: 1px;
`;
export const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: ${breakpoint.mobile_large};
  height: 100%;
  margin: 0 auto;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
`;

const Intro = styled.p`
  height: 210px;
  margin-top: 40px;
  margin-bottom: 10px;
  padding: 30px 0;
  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  -webkit-mask: linear-gradient(
    180deg,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  font-size: 18px;
  font-weight: 500;
  line-height: 160%;
  white-space: pre-line;
  ${({ theme }) => css`
    color: ${theme.content.content_light_2};
  `}
  ${(props) => props.theme.mixins.scroll.none}
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 16px;
  background: linear-gradient(
    180deg,
    transparent,
    ${({ theme }) => theme.backgroundColor.bg_dark_5_alpha} 23%
  );
`;

const GameIntroContainer = styled.div`
  display: flex;
  position: absolute;
  z-index: 10;
  bottom: 0;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  margin-bottom: 30px;
  padding: 0 24px;
`;

const StyledButton = styled.div<{ type: string; width: number | null }>`
  ${({ theme, type, width }) => css`
    width: ${width ? `${width}px` : '100%'};
    height: fit-content;
    padding: 12px;
    border: 1px solid ${theme.stroke.stroke_1};
    border-radius: 22px;
    background-color: ${type === 'light'
      ? theme.colors.white
      : theme.backgroundColor.bg_light_5_alpha};
    color: ${type === 'light'
      ? theme.content.content_dark_1
      : theme.colors.white};
    font-size: 18px;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
  `}
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
